import { useMemo } from "react"
import type { Tab, FlowMode } from "~/react/components/email_accounts/types"
import { getTabOrder } from "~/react/components/email_accounts/utils"

interface UseTabNavigationReturn {
  goToNextTab: (currentTab: Tab) => Tab
  isStepCompleted: (currentTab: Tab, stepTab: Tab) => boolean
  getStepNumber: (stepTab: Tab) => number
}

/**
 * Hook for managing tab navigation logic in the provisioning flow.
 */
export const useTabNavigation = (flowMode?: FlowMode): UseTabNavigationReturn => {
  const tabOrder = useMemo(() => getTabOrder(flowMode), [flowMode])

  const goToNextTab = useMemo(
    () =>
      (currentTab: Tab): Tab => {
        const idx = tabOrder.indexOf(currentTab)
        const next = tabOrder[Math.min(idx + 1, tabOrder.length - 1)]
        return next !== currentTab ? next : currentTab
      },
    [tabOrder],
  )

  const isStepCompleted = useMemo(
    () =>
      (currentTab: Tab, stepTab: Tab): boolean => {
        const currentIdx = tabOrder.indexOf(currentTab)
        const stepIdx = tabOrder.indexOf(stepTab)
        return stepIdx < currentIdx
      },
    [tabOrder],
  )

  const getStepNumber = useMemo(
    () =>
      (stepTab: Tab): number => {
        return tabOrder.indexOf(stepTab) + 1
      },
    [tabOrder],
  )

  return {
    goToNextTab,
    isStepCompleted,
    getStepNumber,
  }
}
